export const SCHEMA_VERSION = 4 as const;
export const SDK_VERSION = '2.7.0';

export type TrackingRegion = 'US' | 'EU';
export type TrackingPrivacyMode = 'anonymous' | 'persistent';
export type TrackingPolicyBasis = 'notice_and_opt_out' | 'opt_in';

export type JsonPrimitive = boolean | number | string | null;
/** Flat business metadata. Identity and form values are not event properties. */
export type CustomEventProperties = Readonly<Record<string, JsonPrimitive>>;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };

export type TrackerConfig = {
  workspaceId: string;
  region: TrackingRegion;
  endpoint: string;
  debugOn: boolean;
  respectDoNotTrack: boolean;
  privacyMode: TrackingPrivacyMode;
  policyBasis: TrackingPolicyBasis;
  flushIntervalMs: number;
  sessionTimeoutMs: number;
  linkedDomains: readonly string[];
  linkAnonymousSessions: boolean;
};

export type TrackerInitOptions = {
  workspaceId: string;
  region: TrackingRegion;
  endpoint?: string;
  respectDoNotTrack?: boolean;
  privacyMode?: TrackingPrivacyMode;
  policyBasis?: TrackingPolicyBasis;
  flushIntervalMs?: number;
  sessionTimeoutMs?: number;
  /** Trusted hostnames or *.domain rules for all subdomains. Defaults to no cross-host linking. */
  linkedDomains?: string[];
  /** Defaults to true. When false, only persistent sessions continue on linked hosts. */
  linkAnonymousSessions?: boolean;
};

export type BrowserContext = {
  userAgent: string;
  webdriver: boolean;
  languages: string[];
  timezone: string | null;
};

export type BrowserFingerprint = {
  provider: 'fingerprintjs';
  visitorId: string;
  version: string;
  confidence: number;
};

export type BrowserThumbmark = {
  hash: string;
  version: string;
};

/** Ids other tools keep in the site's own cookies; only the ones that are set. */
export type ExternalIds = {
  hubspotutk?: string;
  ga4ClientId?: string;
  fbp?: string;
  fbc?: string;
};

export type EnvironmentContext = {
  browser: BrowserContext;
  fingerprint?: BrowserFingerprint;
  thumbmark?: BrowserThumbmark;
  externalIds?: ExternalIds;
};

export type PageContext = {
  url: string;
  path: string;
  title: string;
  referrer: string | null;
};

export type Activity = {
  type: string;
  timestamp: string;
  page: PageContext;
  properties: JsonObject;
};

export type ActivityPayload = {
  schemaVersion: typeof SCHEMA_VERSION;
  sdkVersion: string;
  workspaceId: string;
  privacyMode: TrackingPrivacyMode;
  policyBasis: TrackingPolicyBasis;
  visitorId: string;
  sessionId: string;
  /**
   * The referrer of the page that started the session; null when it had
   * none. Omitted when the SDK does not know it.
   */
  sessionReferrer?: string | null;
  sentAt: string;
  context: EnvironmentContext;
  traits: JsonObject;
  activities: Activity[];
};

export type TrackerLifecycle =
  | { status: 'idle' }
  | { status: 'starting'; workspaceId: string; privacyMode: TrackingPrivacyMode; policyBasis: TrackingPolicyBasis }
  | { status: 'running'; workspaceId: string; privacyMode: TrackingPrivacyMode; policyBasis: TrackingPolicyBasis; visitorId: string; sessionId: string }
  | { status: 'disabled'; reason: 'do-not-track' | 'opt-out' }
  | { status: 'stopped'; workspaceId: string };

export type FlushOptions = {
  preferBeacon?: boolean;
};

export type SendResult =
  | { status: 'sent'; transport: 'beacon' | 'fetch' }
  | { status: 'dropped'; reason: string }
  | { status: 'failed'; reason: string; retryAfterMs?: number };

export interface ActivityTransport {
  send(payload: ActivityPayload, options?: FlushOptions): Promise<SendResult>;
}

export interface UplaneApi {
  readonly version: string;
  init(options: TrackerInitOptions): Promise<void>;
  /** Synchronous calls during a click share its SDK interaction ID and timestamp. Deferred calls are independent. */
  track(name: string, properties?: CustomEventProperties): Promise<void>;
  identify(userId: string, traits?: Record<string, unknown>): void;
  setTraits(traits: Record<string, unknown>): void;
  flush(): Promise<void>;
  grantConsent(): Promise<void>;
  revokeConsent(): Promise<void>;
  optIn(): Promise<void>;
  optOut(): void;
  stop(): void;
  getState(): TrackerLifecycle;
  /**
   * Fresh packed browser context for a server event's `_upl` field. Read it
   * at submission and forward it unchanged. Null unless the tracker runs.
   * This does not require cross-domain linking. An expired session rotates.
   */
  getContext(): string | null;
  /**
   * The browser id that each activity batch sends as `visitorId`, never the
   * identify() user id. Null unless the tracker runs. In anonymous mode the id
   * has the prefix `anon:`, which states that it is an in-memory id of an
   * anonymous browser.
   */
  getDistinctId(): string | null;
  /**
   * The session id that each activity batch sends as `sessionId`. Null unless
   * the tracker runs. In anonymous mode the id has the prefix `anon:`, which
   * states that it is an in-memory id of an anonymous browser.
   */
  getSessionId(): string | null;
  /**
   * Adds the cross-domain session parameter when `url` points to a linked
   * host and the tracker runs. Returns `url` unchanged in every other case.
   */
  decorateUrl(url: string): string;
}
